import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useMaxWidth } from '../../hooks/useMaxWidth';
import { formatDueDateInputText, parseDueDateInput } from '../../utils/taskHelpers';

type DueDateTimeFieldProps = {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  id?: string;
};

export function DueDateTimeField({ value, onChange, required, id }: DueDateTimeFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const pickerRef = useRef<HTMLInputElement>(null);
  const isMobilePicker = useMaxWidth(900);
  const [text, setText] = useState(() => formatDueDateInputText(value));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setText(formatDueDateInputText(value));
  }, [value, editing]);

  const commitText = useCallback(() => {
    const parsed = parseDueDateInput(text);
    if (parsed) {
      onChange(parsed);
      setText(formatDueDateInputText(parsed));
    } else {
      setText(formatDueDateInputText(value));
    }
    setEditing(false);
  }, [text, value, onChange]);

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // Safari may throw when not triggered from a direct gesture; fall back to click.
      }
    }
    el.click();
  };

  return (
    <div className={`task-due-field${isMobilePicker ? ' task-due-field--mobile-picker' : ''}`}>
      <input
        id={fieldId}
        className="field-input task-due-field__text"
        type="text"
        value={text}
        onChange={(e) => {
          setEditing(true);
          setText(e.target.value);
        }}
        onBlur={commitText}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder="e.g. 30/08/2026, 2:54 pm"
        required={required}
        autoComplete="off"
      />
      <div className="task-due-field__picker-wrap">
        {isMobilePicker ? (
          <>
            <span className="task-due-field__picker-btn" aria-hidden="true">
              <span className="task-due-field__picker-icon">📅</span>
            </span>
            <input
              ref={pickerRef}
              className="task-due-field__picker-native"
              type="datetime-local"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              aria-label="Pick date and time"
            />
          </>
        ) : (
          <>
            <button
              type="button"
              className="task-due-field__picker-btn"
              onClick={openPicker}
              aria-label="Open date and time picker"
              title="Pick date & time"
            >
              <span className="task-due-field__picker-icon" aria-hidden="true">
                📅
              </span>
            </button>
            <input
              ref={pickerRef}
              className="task-due-field__picker-native"
              type="datetime-local"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
            />
          </>
        )}
      </div>
      <p className="field-hint task-due-field__hint">Type to edit, or tap the calendar to pick.</p>
    </div>
  );
}
